import moment from "moment-timezone";

/**
 * Reading and writing DSA schedule event times.
 *
 * An event stores a fixed wall clock: `startTime` / `endTime` carry the time of day with no timezone
 * attached, and `startDate` / `endDate` carry the same wall clock pinned to UTC. It therefore renders
 * identically in every browser and plays at that wall clock local to whichever screen it runs on.
 *
 * Every read and write of an event's time goes through this module — no component should do date math
 * on `startDate` directly.
 */

/** The time-carrying subset of DsaScheduleEventDto, which a DTO satisfies structurally. */
export type ScheduleEventTimes = {
  startDate?: number;
  endDate?: number;
  startTime?: string;
  endTime?: string;
};

/**
 * Encodes a wall clock as the millis we store for an event. Pinning to UTC is what makes the value
 * independent of the browser that wrote it.
 *
 * Keep this the only writer: the backend reads the calendar date from these millis and the time of
 * day from `startTime` / `endTime`, so the two must always agree.
 */
export function toStoredMillis(dateStr: string, timeStr: string): number {
  const [year, month, day] = dateStr.split("-").map(Number);
  const [hours, minutes, seconds = 0] = timeStr.split(":").map(Number);
  return Date.UTC(year, month - 1, day, hours, minutes, seconds);
}

/**
 * The wall clock to show for an event — the same times for every viewer, in every timezone.
 *
 * The moments are held in UTC and never converted, which is what keeps them stable no matter who is
 * looking and where from.
 */
export function eventWallClock(ev: ScheduleEventTimes): { start: moment.Moment; end: moment.Moment } {
  return { start: moment.utc(ev.startDate), end: moment.utc(ev.endDate) };
}

/**
 * The millis of a wall-clock moment with its timezone discarded, in the same UTC-pinned encoding
 * [toStoredMillis] produces.
 *
 * Use it to put an event into the frame the user reads off the screen, for things like overlap
 * detection.
 */
export function wallClockMillis(m: moment.Moment): number {
  return Date.UTC(m.year(), m.month(), m.date(), m.hour(), m.minute(), m.second());
}

/**
 * "Now" as a wall clock in the same frame [eventWallClock] returns, so the two can be compared.
 *
 * An event plays on the screen's clock, so that is the clock read here. Falls back to the viewer's
 * timezone when the screen's is missing.
 */
export function nowInScreenFrame(nowMs: number, screenTimezone?: string): number {
  const zone = knownZone(screenTimezone);
  return wallClockMillis(zone ? moment.tz(nowMs, zone) : moment(nowMs));
}

/** The zone name if moment recognises it, otherwise undefined — guards empty and stale zone names. */
function knownZone(timezone?: string): string | undefined {
  return timezone && moment.tz.zone(timezone) ? timezone : undefined;
}
